import React, { useState, useMemo } from "react";
import { Eye } from "lucide-react";
import Button from "@/components/ui/Button";
import CompanyLogo from "@/assets/Dashboard_Card.png";
import DashboardCard from "@/assets/Dashboard_Card.png";
import Dropdown from "@/components/ui/DropDown";
import Icon1 from "@/assets/home/card/Icon1.svg";
import Icon2 from "@/assets/home/card/Icon2.svg";
import Icon3 from "@/assets/home/card/Icon3.svg";
import Icon4 from "@/assets/home/card/Icon4.svg";
import Icon5 from "@/assets/home/card/Icon5.svg";
import Icon6 from "@/assets/home/card/Icon6.svg";
import useCampaignDetailStore from "../store/useCampaignDetailStore";
import { useNavigate } from "react-router-dom";
import useGlobalStore from "@/store/globalStore";

const CampaignDashboardVisualView: React.FC = () => {
  const { campaignDetail, putS3Image, uploadDashboardImage } =
    useCampaignDetailStore();
  const { showSuccess, showError } = useGlobalStore();
  const [selectedFlight, setSelectedFlight] = useState<string>("");
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Prepare flight filter options
  const flightOptions = useMemo(() => {
    if (!campaignDetail) return [];

    const options: { title: string; value: string }[] = [];
    campaignDetail.nested_by_flight.forEach((flight) => {
      options.push({
        title: `Flight ${flight.flight_number}`,
        value: flight.flight_id,
      });
    });
    return options;
  }, [campaignDetail]);

  // Prepare language filter options
  const languageOptions = useMemo(() => {
    if (!campaignDetail) return [];

    const options: { title: string; value: string }[] = [];
    campaignDetail.nested_by_language_objective.forEach((language) => {
      options.push({
        title: language.language_name,
        value: language.language_id,
      });
    });
    return options;
  }, [campaignDetail]);

  // Filter the data based on selected filters
  const filteredData = useMemo(() => {
    if (!campaignDetail) return [];

    let data = campaignDetail.nested_by_language_objective;

    // Filter by language
    if (selectedLanguage) {
      data = data.filter((lang) => lang.language_id === selectedLanguage);
    }

    // Filter by flight if needed
    if (selectedFlight) {
      data = data.map((language) => ({
        ...language,
        objectives: language.objectives.map((objective) => ({
          ...objective,
          assets: objective.assets.filter(
            (asset) => asset.flight_id === selectedFlight,
          ),
          metrics: {
            ...objective.metrics,
            total_assets: objective.assets.filter(
              (asset) => asset.flight_id === selectedFlight,
            ).length,
          },
        })),
      }));
    }

    return data;
  }, [campaignDetail, selectedFlight, selectedLanguage]);

  // Handle thumbnail upload
  const handleThumbnailClick = () => {
    fileInputRef.current?.click();
  };

  // Upload handler (commented out as per original code) Monil's Code starts
  // const handleFileChange = async (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const file = event.target.files?.[0];
  //   if (!file || !campaignDetail) return;

  //   try {
  //     setIsUploading(true);

  //     const campaignId = campaignDetail.metadata.campaign.id;

  //     // Generate the key for the image
  //     const key = `${campaignId}/${file.name}`;

  //     // Prepare the request body
  //     const requestBody = {
  //       campaign_id: campaignId,
  //       type: "campaign_dashboard",
  //       key: key,
  //       mime: file.type,
  //     };

  //     // First, call the backend to confirm the header image
  //     const response = await uploadDashboardImage(campaignId, requestBody);

  //     // If the response contains a presigned URL, upload the file to S3
  //     if (response.data.url) {
  //       await putS3Image(response.data.url, file);
  //     }

  //     // Optionally refresh the campaign data to show the new image
  //     // await getCampaignDetailData(campaignId);

  //     showSuccess("Image uploaded successfully!");
  //   } catch (error) {
  //     console.error("Error uploading thumbnail:", error);
  //     const errorMessage =
  //       error instanceof Error
  //         ? error.message
  //         : "Failed to upload thumbnail. Please try again.";
  //     showError(errorMessage);
  //   } finally {
  //     setIsUploading(false);
  //     // Reset file input
  //     if (fileInputRef.current) {
  //       fileInputRef.current.value = "";
  //     }
  //   }
  // };
  // Monil's Code ends

  // Upload handler (New Code Starts)
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !campaignDetail) return;

    try {
      setIsUploading(true);

      const campaignId = campaignDetail.metadata.campaign.id;

      // ---------- STEP 1: INITIATE (ask backend for presigned PUT URL) ----------
      // IMPORTANT: send `filename`, NOT `key`
      const initBody = {
        campaign_id: campaignId,
        type: "campaign_dashboard",
        filename: file.name, // ✅ use filename to trigger initiation
        mime: file.type, // ✅ this must match what you'll send in PUT
      };

      const initRes = await uploadDashboardImage(campaignId, initBody);

      // Backend should return: { upload_url, key, mime }
      const uploadUrl =
        "upload_url" in initRes.data
          ? initRes.data.upload_url
          : initRes.data.url;
      const { key } = initRes.data;

      if (!uploadUrl || !key) {
        throw new Error("Upload initiation failed: missing upload URL or key");
      }

      // ---------- STEP 2: PUT to S3 using the presigned URL ----------
      // VERY IMPORTANT: raw file body + exact Content-Type
      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      // ---------- STEP 3: CONFIRM (tell backend to create DB record) ----------
      const confirmBody = {
        campaign_id: campaignId,
        type: "campaign_dashboard",
        key, // ✅ SAME key returned from Step 1
        mime: file.type, // keep for DB record / validation
      };

      await uploadDashboardImage(campaignId, confirmBody);

      showSuccess("Image uploaded successfully!");
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to upload thumbnail. Please try again.";
      showError(errorMessage);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };
  // New Code Ends

  if (!campaignDetail) {
    return <div></div>;
  }

  return (
    <div>
      {/* Filters and Content */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="font-semibold">Filter by:</span>
          <Dropdown
            options={flightOptions}
            selected={selectedFlight}
            setSelected={(value) => {
              setSelectedFlight(value);
            }}
            placeholder="All Flights"
            className="min-w-36"
          />
          <Dropdown
            options={languageOptions}
            selected={selectedLanguage}
            setSelected={(value) => {
              setSelectedLanguage(value);
            }}
            placeholder="All Languages"
            className="min-w-36"
          />

          {/* Clear Filters Button */}
          {(selectedFlight || selectedLanguage) && (
            <button
              onClick={() => {
                setSelectedFlight("");
                setSelectedLanguage("");
              }}
              className="text-sm text-pink-600 hover:text-pink-700 underline font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>

        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            onClick={handleThumbnailClick}
            disabled={isUploading}
            className="text-gray-500 hover:text-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="flex items-center gap-2 text-sm underline">
              <span className="mr-3">
                {isUploading ? "Uploading..." : "Edit Thumbnail"}
              </span>
              <img
                src={
                  campaignDetail.metadata.header_image?.url
                    ? campaignDetail.metadata.header_image.url
                    : CompanyLogo
                }
                className="w-10 h-10 rounded"
              />
            </span>
          </button>
        </div>
      </div>

      {/* Language Sections - Dynamically rendered */}
      <div className="space-y-8">
        {filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <p className="text-lg font-medium mb-2">No data available</p>
            <p className="text-sm">
              Try adjusting your filters to see more results
            </p>
            {(selectedFlight || selectedLanguage) && (
              <button
                onClick={() => {
                  setSelectedFlight("");
                  setSelectedLanguage("");
                }}
                className="mt-4 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
              >
                Clear All Filters
              </button>
            )}
          </div>
        ) : (
          filteredData.map((language) => {
            // Filter objectives before rendering to avoid returning null
            const visibleObjectives = language.objectives.filter(
              (objective) =>
                !selectedFlight || objective.metrics.total_assets > 0,
            );

            // Skip rendering the entire language section if no objectives are visible
            if (visibleObjectives.length === 0) {
              return null;
            }

            return (
              <div key={language.language_id}>
                <h3 className="text-2xl font-bold mb-4">
                  {language.language_name}
                </h3>
                <hr className="mt-2 border-gray-300 mb-8" />
                <div className="grid grid-cols-3 gap-14">
                  {visibleObjectives.map((objective) => (
                    <div key={objective.objective_id}>
                      <div className="mb-2 flex justify-between">
                        <span className="font-semibold">
                          {objective.objective_name}
                        </span>
                        <span className="text-gray-600">
                          {objective.metrics.total_assets}
                        </span>
                      </div>
                      <AssetsCard
                        imgUrl={objective.header_image?.url || DashboardCard}
                        progress1={objective.status_counts?.briefed ?? 0}
                        progress2={objective.status_counts?.in_progress ?? 0}
                        progress3={objective.status_counts?.in_review ?? 0}
                        progress4={objective.status_counts?.approved ?? 0}
                        progress5={objective.status_counts?.trafficking ?? 0}
                        progress6={objective.status_counts?.live ?? 0}
                        campaignId={campaignDetail.metadata.campaign.id}
                        languageId={language.language_id}
                        objectiveId={objective.objective_id}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

interface AssetsCardProps {
  imgUrl: string;
  progress1: number;
  progress2: number;
  progress3: number;
  progress4: number;
  progress5: number;
  progress6: number;
  campaignId: string;
  languageId: string;
  objectiveId: string;
}

const AssetsCard: React.FC<AssetsCardProps> = ({
  imgUrl,
  progress1,
  progress2,
  progress3,
  progress4,
  progress5,
  progress6,
  campaignId,
  languageId,
  objectiveId,
}) => {
  const navigate = useNavigate();

  const [hover, setHover] = useState(false);
  const icons = [
    [
      { src: Icon2, alt: "icon 2", number: progress1 },
      { src: Icon3, alt: "icon 3", number: progress2 },
      { src: Icon4, alt: "icon 4", number: progress3 },
    ],
    [
      { src: Icon1, alt: "icon 1", number: progress4 },
      { src: Icon6, alt: "icon 6", number: progress5 },
      { src: Icon5, alt: "icon 5", number: progress6 },
    ],
  ];

  return (
    <div
      className="w-full rounded-2xl relative hover-card h-[330px] overflow-hidden"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Background Image with Zoom Effect */}
      <div
        className="bg-cover bg-no-repeat bg-center w-full h-full absolute inset-0 transition-transform duration-500 ease-out"
        style={{
          backgroundImage: `url(${imgUrl})`,
          transform: hover ? "scale(1.1)" : "scale(1)",
        }}
      />

      {/* Overlay */}
      <div
        className={`w-full h-full p-0 transition-opacity duration-300 absolute inset-0 rounded-2xl hover-overlay ${
          hover ? "opacity-100 bg-black bg-opacity-70" : "opacity-0"
        }`}
      >
        <div className="p-0 flex w-full h-full flex-col gap-7 justify-center items-center card-drop rounded-2xl">
          <div className="flex w-full justify-center z-10 relative">
            {icons.map((iconGroup, idx) => (
              <div
                key={idx}
                className="flex flex-col gap-3.5 items-center justify-center"
              >
                {iconGroup.map(({ src, alt, number }, i) => (
                  <div key={i} className="flex mr-6 ml-4 my-1">
                    <img
                      src={src}
                      alt={alt}
                      className="w-8 h-8 filter drop-shadow-lg"
                    />
                    <p className="w-8 h-8 flex justify-center items-center text-white">
                      {number}
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <Button
            className="z-10 relative"
            type="outline"
            trailingIcon={<Eye />}
            onClick={() => {
              navigate(
                `/campaign/${campaignId}/ad-category?languageId=${languageId}&objectiveId=${objectiveId}`,
              );
            }}
          >
            View Assets
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CampaignDashboardVisualView;
